'use client';

import Image from 'next/image';
import Link from 'next/link';
import { useMemo, useState } from 'react';
import { formatPrice } from '@/lib/shopify';
import type { Product } from '@/lib/shopify/types';

interface DesignProductChooserProps {
  products: Product[];
  selectedHandle: string;
}

function getProductGroup(product: Product): string {
  return product.productType || 'Other';
}

function getProductHref(handle: string): string {
  return `/design-your-own?product=${handle}`;
}

function ProductItem({
  product,
  selected,
  onSelect,
}: {
  product: Product;
  selected: boolean;
  onSelect?: () => void;
}) {
  return (
    <Link
      href={getProductHref(product.handle)}
      onClick={onSelect}
      className={`grid grid-cols-[52px_minmax(0,1fr)] gap-3 overflow-hidden border p-2 transition-colors ${
        selected
          ? 'border-brand-blue bg-brand-blue text-white'
          : 'border-[var(--color-border)] bg-white text-black hover:border-black'
      }`}
    >
      <span className="relative block aspect-square overflow-hidden bg-[var(--color-off-white)]">
        {product.featuredImage ? (
          <Image
            src={product.featuredImage.url}
            alt={product.featuredImage.altText || product.title}
            fill
            className="object-cover"
            sizes="52px"
          />
        ) : (
          <span className="flex h-full items-center justify-center text-[10px] text-gray-400">
            No image
          </span>
        )}
      </span>
      <span className="min-w-0 overflow-hidden">
        <span className="block truncate text-sm font-semibold">{product.title}</span>
        <span className={`block text-xs ${selected ? 'text-white/70' : 'text-[var(--color-text-secondary)]'}`}>
          From {formatPrice(product.priceRange.minVariantPrice)}
        </span>
      </span>
    </Link>
  );
}

function ProductList({
  products,
  selectedHandle,
  onSelect,
}: {
  products: Product[];
  selectedHandle: string;
  onSelect?: () => void;
}) {
  const groupedProducts = useMemo(() => {
    const groups = new Map<string, Product[]>();
    products.forEach((product) => {
      const group = getProductGroup(product);
      const current = groups.get(group) || [];
      groups.set(group, [...current, product]);
    });

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([group, groupProducts]) => [
        group,
        groupProducts.slice().sort((a, b) => a.title.localeCompare(b.title)),
      ] as const);
  }, [products]);

  if (products.length === 0) {
    return (
      <div className="border border-[var(--color-border)] bg-[var(--color-off-white)] px-4 py-6 text-center text-sm text-[var(--color-text-secondary)]">
        No matching products.
      </div>
    );
  }

  return (
    <div>
      {groupedProducts.map(([group, groupProducts]) => (
        <div key={group} className="mb-5 last:mb-0">
          <h2 className="mb-2 px-1 text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
            {group}
          </h2>
          <div className="space-y-2">
            {groupProducts.map((product) => (
              <ProductItem
                key={product.id}
                product={product}
                selected={product.handle === selectedHandle}
                onSelect={onSelect}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function DesignProductChooser({ products, selectedHandle }: DesignProductChooserProps) {
  const [query, setQuery] = useState('');
  const [group, setGroup] = useState('all');
  const [mobileOpen, setMobileOpen] = useState(false);

  const selectedProduct = products.find((product) => product.handle === selectedHandle) || products[0];
  const groups = useMemo(
    () => ['all', ...Array.from(new Set(products.map(getProductGroup))).sort((a, b) => a.localeCompare(b))],
    [products]
  );

  const filteredProducts = useMemo(() => {
    const q = query.trim().toLowerCase();
    return products.filter((product) => {
      const matchesGroup = group === 'all' || getProductGroup(product) === group;
      if (!matchesGroup) return false;
      if (!q) return true;
      return `${product.title} ${product.productType} ${product.handle}`.toLowerCase().includes(q);
    });
  }, [group, products, query]);

  const controls = (
    <div className="space-y-3">
      <label className="block">
        <span className="sr-only">Search products</span>
        <input
          type="search"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search all products"
          className="w-full border border-[var(--color-border)] px-3 py-2 text-sm text-black focus:border-black focus:outline-none"
        />
      </label>
      <label className="block">
        <span className="sr-only">Filter category</span>
        <select
          value={group}
          onChange={(event) => setGroup(event.target.value)}
          className="w-full border border-[var(--color-border)] bg-white px-3 py-2 text-sm text-black focus:border-black focus:outline-none"
        >
          {groups.map((item) => (
            <option key={item} value={item}>
              {item === 'all' ? 'All categories' : item}
            </option>
          ))}
        </select>
      </label>
    </div>
  );

  return (
    <>
      <aside className="design-product-chooser-desktop">
        <div className="overflow-hidden border border-[var(--color-border)] bg-white">
          <div className="border-b border-[var(--color-border)] p-4">
            <h1 className="text-xl font-bold uppercase tracking-tight">Design Your Own</h1>
            <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
              {products.length} Shopify products mapped
            </p>
            <div className="mt-4">{controls}</div>
          </div>
          <div className="max-h-[70vh] overflow-y-auto p-3">
            <ProductList products={filteredProducts} selectedHandle={selectedHandle} />
          </div>
        </div>
      </aside>

      <section className="design-product-chooser-mobile">
        <div className="overflow-hidden border border-[var(--color-border)] bg-white">
          <button
            type="button"
            onClick={() => setMobileOpen((open) => !open)}
            className="grid w-full grid-cols-[56px_minmax(0,1fr)_24px] items-center gap-3 p-4 text-left"
            aria-expanded={mobileOpen}
          >
            <span className="relative block aspect-square overflow-hidden bg-[var(--color-off-white)]">
              {selectedProduct?.featuredImage ? (
                <Image
                  src={selectedProduct.featuredImage.url}
                  alt={selectedProduct.featuredImage.altText || selectedProduct.title}
                  fill
                  className="object-cover"
                  sizes="56px"
                />
              ) : (
                <span className="flex h-full items-center justify-center text-[10px] text-gray-400">
                  No image
                </span>
              )}
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                {products.length} Shopify products mapped
              </span>
              <span className="block truncate text-base font-bold text-black">
                {selectedProduct?.title || 'Choose a product'}
              </span>
              {selectedProduct && (
                <span className="block text-sm text-[var(--color-text-secondary)]">
                  From {formatPrice(selectedProduct.priceRange.minVariantPrice)}
                </span>
              )}
            </span>
            <svg
              className={`h-5 w-5 text-black transition-transform ${mobileOpen ? 'rotate-180' : ''}`}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
            </svg>
          </button>

          {mobileOpen && (
            <div className="border-t border-[var(--color-border)] p-3">
              {controls}
              <div className="mt-3 max-h-[360px] overflow-y-auto">
                <ProductList
                  products={filteredProducts}
                  selectedHandle={selectedHandle}
                  onSelect={() => setMobileOpen(false)}
                />
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
